import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { InteractionManager, Platform } from 'react-native';
import { GroceryItem, GroceryListState, WasteReductionMetrics, GroceryCategory } from '../types/groceryList';
import { PlannedMeal, getCurrentMondayStart, selectMealsForDateRange } from './mealPlanSlice';
import { addDays, todayIso } from '../utils/localDate';
import { InventoryItem } from './inventorySlice';
import { generateGroceryList, generateWeekSnapshot, categorizeIngredient, normalizeIngredientName } from '../services/groceryListService';
import { isFirebaseEnabled } from '../services/firebaseConfig';
import { isRecipeMeal } from './mealEntry';
import { trackEvent } from '../services/analyticsService';
import {
  fetchGroceryListFromCloud,
  mergeGroceryLists,
  GroceryListSyncResult,
} from '../services/groceryListSyncService';

const initialState: GroceryListState = {
  items: [],
  alreadyHaveItems: [],
  metrics: null,
  generatedDate: null,
  weekSnapshot: '',
  sourceWeekStart: null,
  coversUntil: null,
  customItems: [],
  isStale: false,
  isRegenerating: false,
  syncLoading: false,
  syncError: null,
};

/**
 * Download grocery list from Firestore and merge with local state
 */
export const downloadGroceryListFromCloud = createAsyncThunk(
  'groceryList/downloadFromCloud',
  async (userId: string, { getState, rejectWithValue }) => {
    try {
      if (!isFirebaseEnabled()) {
        throw new Error('Firebase not enabled');
      }

      // Read householdId from auth state so paired users pull from
      // households/{hid}/... instead of users/{uid}/...
      const fullState = getState() as { groceryList: GroceryListState; auth?: { householdId?: string | null } };
      const householdId = fullState.auth?.householdId ?? null;

      const cloudData = await fetchGroceryListFromCloud(userId, householdId);

      // Get current local state
      const state = getState() as { groceryList: GroceryListState };
      const local = {
        items: state.groceryList.items,
        customItems: state.groceryList.customItems,
        generatedDate: state.groceryList.generatedDate,
        weekSnapshot: state.groceryList.weekSnapshot,
        sourceWeekStart: state.groceryList.sourceWeekStart ?? null,
        coversUntil: state.groceryList.coversUntil ?? null,
      };

      // Merge local and cloud
      const merged = mergeGroceryLists(local, cloudData);

      return merged;
    } catch (error: any) {
      console.error('Failed to download grocery list from cloud:', error);
      return rejectWithValue(error.message || 'Download failed');
    }
  }
);

/**
 * Helper to generate unique ID
 */
function generateId(): string {
  return `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

const groceryListSlice = createSlice({
  name: 'groceryList',
  initialState,
  reducers: {
    /**
     * Apply an already-computed grocery list.
     *
     * The expensive part (ingredient parsing, normalization, unit conversion,
     * inventory matching) deliberately happens OUTSIDE this reducer — see the
     * `buildGroceryList` thunk below. Running it here blocked the JS thread
     * inside `dispatch()`, which froze the planner for seconds at a time.
     */
    setGeneratedList: (
      state,
      action: PayloadAction<{
        items: GroceryItem[];
        alreadyHaveItems: GroceryItem[];
        metrics: WasteReductionMetrics;
        weekSnapshot: string;
        sourceWeekStart: string;
        /** Omitted keeps the list's current range; see GroceryListState.coversUntil. */
        coversUntil?: string | null;
      }>
    ) => {
      const { alreadyHaveItems, metrics, weekSnapshot, sourceWeekStart } = action.payload;
      // Copy: the payload is applied into Immer state below and mutated in place.
      const items = action.payload.items.map(i => ({ ...i }));

      // Preserve purchased (checked) items across regenerations: checking an
      // item put it in the pantry, so the generator now classifies it as
      // "already have" — but it must stay visible, struck through, until the
      // user taps "Ranger les achats".
      const previousChecked = (state.items || []).filter(i => i.isChecked && !i.isCustom);
      const keyOf = (name: string) => name.trim().toLowerCase();
      const newNames = new Set(items.map(i => keyOf(i.name)));
      for (const prev of previousChecked) {
        const regenerated = items.find(i => keyOf(i.name) === keyOf(prev.name));
        if (regenerated) {
          regenerated.isChecked = true;
        } else {
          // Dropped by the generator (now in pantry) — keep it listed as purchased
          items.push({ ...prev });
          newNames.add(keyOf(prev.name));
        }
      }
      // Avoid double-listing a purchased item in the collapsed already-have section
      const filteredAlreadyHave = alreadyHaveItems.filter(
        a => !previousChecked.some(p => keyOf(p.name) === keyOf(a.name))
      );

      // Update state
      state.items = items;
      state.alreadyHaveItems = filteredAlreadyHave;
      state.metrics = metrics;
      state.generatedDate = new Date().toISOString();
      state.weekSnapshot = weekSnapshot;
      state.sourceWeekStart = sourceWeekStart;
      if (action.payload.coversUntil !== undefined) {
        state.coversUntil = action.payload.coversUntil;
      }

      // Preserve custom items
      if (state.customItems?.length > 0) {
        state.items.push(...state.customItems);
      }

      // Reset stale/regenerating flags
      state.isStale = false;
      state.isRegenerating = false;
    },

    /**
     * Mark the grocery list as stale (meal plan has changed)
     */
    setStale: (state, action: PayloadAction<boolean>) => {
      state.isStale = action.payload;
    },

    /**
     * Mark the grocery list as regenerating
     */
    setRegenerating: (state, action: PayloadAction<boolean>) => {
      state.isRegenerating = action.payload;
    },

    /**
     * Toggle item checked status
     */
    toggleItem: (state, action: PayloadAction<string>) => {
      const itemId = action.payload;
      const item = state.items.find(i => i.id === itemId);

      if (item) {
        item.isChecked = !item.isChecked;
      }
    },

    /**
     * "Ranger les achats" — archive purchased items. They already entered the
     * pantry when checked; this clears them from the visible list.
     */
    clearPurchased: (state) => {
      state.items = state.items.filter(i => !i.isChecked);
      state.customItems = (state.customItems || []).filter(i => !i.isChecked);
    },

    /**
     * Add custom item to the list
     */
    addCustomItem: (
      state,
      action: PayloadAction<{
        name: string;
        category: GroceryCategory;
        amount?: number;
        unit?: string;
      }>
    ) => {
      const { name, category, amount, unit } = action.payload;

      const customItem: GroceryItem = {
        id: generateId(),
        name,
        original: amount && unit ? `${amount} ${unit} ${name}` : name,
        amount,
        unit,
        category,
        isChecked: false,
        isCustom: true,
        recipeIds: [],
        recipeNames: [],
      };

      state.items.push(customItem);
      state.customItems.push(customItem);
    },

    /**
     * Remove custom item from the list
     */
    removeCustomItem: (state, action: PayloadAction<string>) => {
      const itemId = action.payload;

      // Remove from main items
      state.items = state.items.filter(item => item.id !== itemId);

      // Remove from custom items
      state.customItems = state.customItems.filter(item => item.id !== itemId);
    },

    /**
     * Clear entire grocery list
     */
    clearList: (state) => {
      state.items = [];
      state.alreadyHaveItems = [];
      state.metrics = null;
      state.generatedDate = null;
      state.weekSnapshot = '';
      state.customItems = [];
    },

    /**
     * Update metrics (useful after inventory changes)
     */
    updateMetrics: (
      state,
      action: PayloadAction<{
        meals: PlannedMeal[];
        inventory: InventoryItem[];
      }>
    ) => {
      const { meals, inventory } = action.payload;
      const { metrics } = generateGroceryList(meals, inventory);
      state.metrics = metrics;
    },

    /**
     * Move grocery item to inventory (remove from list)
     * Used when user clicks "I Already Have This"
     */
    moveToInventory: (
      state,
      action: PayloadAction<string>
    ) => {
      const itemId = action.payload;

      // Find the item to move
      const itemIndex = state.items.findIndex(item => item.id === itemId);
      if (itemIndex === -1) return; // Item not found

      const [movedItem] = state.items.splice(itemIndex, 1);

      // Also remove from custom items if applicable
      state.customItems = state.customItems.filter(item => item.id !== itemId);

      // Push to alreadyHaveItems with source: 'manual-review'
      state.alreadyHaveItems.push({
        ...movedItem,
        source: 'manual-review',
      });

      // Rebuild metrics from the updated lists
      if (state.metrics) {
        state.metrics.alreadyHave = state.alreadyHaveItems.length;
        state.metrics.needToBuy = state.items.length;
        state.metrics.totalIngredients = state.items.length + state.alreadyHaveItems.length;
        state.metrics.percentageSaved =
          state.metrics.totalIngredients > 0
            ? (state.metrics.alreadyHave / state.metrics.totalIngredients) * 100
            : 0;
        state.metrics.estimatedSavings = state.metrics.alreadyHave * 3;
      }
    },

    /**
     * Add ingredients from a recipe to the grocery list
     * Smart aggregation: combines same ingredients across recipes
     */
    addIngredientsFromRecipe: (
      state,
      action: PayloadAction<{
        recipeId: string;
        recipeName: string;
        servings: number;
        ingredients: { name: string; original: string; amount?: number; unit?: string }[];
        batchId?: string;
      }>
    ) => {
      const { recipeId, recipeName, servings, ingredients, batchId } = action.payload;

      for (const ingredient of ingredients) {
        // Skip ingredients with no usable name
        const ingredientName = ingredient.name || ingredient.original;
        if (!ingredientName || !ingredientName.trim()) continue;

        const normalizedName = normalizeIngredientName(ingredientName);
        if (!normalizedName) continue;

        // Find existing item with same normalized name
        const existingItem = state.items.find(item => {
          const existingNormalized = normalizeIngredientName(item.name);
          return existingNormalized === normalizedName;
        });

        if (existingItem) {
          // Smart aggregation: add amounts if compatible units
          if (ingredient.amount && existingItem.amount && existingItem.unit === ingredient.unit) {
            existingItem.amount += ingredient.amount;
            existingItem.totalNeeded = (existingItem.totalNeeded || existingItem.amount) + ingredient.amount;
            // Update original text to reflect new total
            existingItem.original = existingItem.amount && existingItem.unit
              ? `${existingItem.amount} ${existingItem.unit} ${existingItem.name}`
              : existingItem.name;
          }

          // Add recipe attribution if not already present
          if (!existingItem.recipeIds.includes(recipeId)) {
            existingItem.recipeIds.push(recipeId);
            existingItem.recipeNames.push(recipeName);
          }
        } else {
          // Create new item
          const category = categorizeIngredient(ingredientName);
          const newItem: GroceryItem = {
            id: generateId(),
            name: ingredient.name || normalizedName,
            original: ingredient.original || ingredientName,
            amount: ingredient.amount,
            unit: ingredient.unit,
            category,
            isChecked: false,
            isCustom: false,
            recipeIds: [recipeId],
            recipeNames: [recipeName],
            totalNeeded: ingredient.amount,
            batchId,
          };
          state.items.push(newItem);
        }
      }

      // Update generated date
      if (!state.generatedDate) {
        state.generatedDate = new Date().toISOString();
      }
    },

    /**
     * Remove all items from a specific recipe
     */
    removeRecipeItems: (
      state,
      action: PayloadAction<string>
    ) => {
      const recipeId = action.payload;

      // For items that belong to multiple recipes, just remove this recipe's attribution
      // For items that belong only to this recipe, remove entirely
      state.items = state.items.filter(item => {
        if (item.recipeIds.includes(recipeId)) {
          const idx = item.recipeIds.indexOf(recipeId);
          item.recipeIds.splice(idx, 1);
          item.recipeNames.splice(idx, 1);
          // Keep item if it still belongs to other recipes
          return item.recipeIds.length > 0 || item.isCustom;
        }
        return true;
      });
    },

    /**
     * Remove all items that were added in a specific batch (for undo support).
     * Only removes newly created items (with matching batchId); merged items are unaffected.
     */
    removeItemsByBatchId: (
      state,
      action: PayloadAction<string>
    ) => {
      state.items = state.items.filter(item => item.batchId !== action.payload);
    },

    /**
     * Apply an incoming Firestore snapshot — used by the live-sync hook.
     * Cloud is the source of truth here: replaces items / customItems /
     * generatedDate / weekSnapshot / sourceWeekStart wholesale.
     */
    hydrateGroceryListFromCloud: (
      state,
      action: PayloadAction<{
        items: GroceryItem[];
        customItems: GroceryItem[];
        generatedDate: string | null;
        weekSnapshot: string;
        sourceWeekStart?: string | null;
        coversUntil?: string | null;
      }>
    ) => {
      state.items = action.payload.items;
      state.customItems = action.payload.customItems;
      state.generatedDate = action.payload.generatedDate;
      state.weekSnapshot = action.payload.weekSnapshot;
      // Absent on documents written before the field existed — keep the local
      // pin rather than clearing it, so a legacy sync doesn't unpin the list.
      if (action.payload.sourceWeekStart !== undefined) {
        state.sourceWeekStart = action.payload.sourceWeekStart;
      }
      if (action.payload.coversUntil !== undefined) {
        state.coversUntil = action.payload.coversUntil;
      }
      state.isStale = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(downloadGroceryListFromCloud.pending, (state) => {
        state.syncLoading = true;
        state.syncError = null;
      })
      .addCase(downloadGroceryListFromCloud.fulfilled, (state, action) => {
        state.syncLoading = false;
        state.items = action.payload.items;
        state.customItems = action.payload.customItems;
        state.generatedDate = action.payload.generatedDate;
        state.weekSnapshot = action.payload.weekSnapshot;
        state.sourceWeekStart = action.payload.sourceWeekStart ?? state.sourceWeekStart;
        if (action.payload.coversUntil !== undefined) {
          state.coversUntil = action.payload.coversUntil;
        }
        console.log(`GroceryList - Synced from cloud, ${action.payload.items.length} items`);
      })
      .addCase(downloadGroceryListFromCloud.rejected, (state, action) => {
        state.syncLoading = false;
        state.syncError = action.payload as string;
        console.error('GroceryList - Cloud sync failed:', action.payload);
      });
  },
});

export const {
  setGeneratedList,
  toggleItem,
  clearPurchased,
  addCustomItem,
  removeCustomItem,
  clearList,
  updateMetrics,
  moveToInventory,
  addIngredientsFromRecipe,
  removeRecipeItems,
  removeItemsByBatchId,
  setStale,
  setRegenerating,
  hydrateGroceryListFromCloud,
} = groceryListSlice.actions;

/**
 * The week the grocery list belongs to.
 *
 * Falls back to the real current week when nothing is pinned yet, or when the
 * pinned week has rolled into the past — otherwise a list pinned to a past week
 * would stay pinned there forever. Derived rather than stored so it self-heals
 * without a startup migration; `setGeneratedList` writes the resolved value back.
 */
export const selectGroceryWeekStart = (state: {
  groceryList?: { sourceWeekStart?: string | null };
}): string => {
  const pinned = state.groceryList?.sourceWeekStart ?? null;
  const currentWeek = getCurrentMondayStart();
  return pinned && pinned >= currentWeek ? pinned : currentWeek;
};

/**
 * The dates the list is built from: the pinned week from `today` on, stretched
 * to `coversUntil` when that runs past its Sunday.
 *
 * A day that is over is not shopped for (#804). The list used to take the
 * whole Monday week, so on a Friday it still opened on Monday's soup: a meal
 * planned ahead stays `planned` once its day passes, and `cooked` was the only
 * thing that kept a meal off the list. Today itself stays in all day — tonight's
 * dinner is still being shopped for.
 *
 * Pure, with `today` passed in, so the hook that watches for staleness and the
 * thunk that builds the list cannot disagree about the meal set — when they did
 * (#548), the list was permanently stale and regenerated forever on an idle tab.
 */
export function groceryRange(
  weekStart: string,
  coversUntil: string | null | undefined,
  today: string,
): { start: string; end: string } {
  const sunday = addDays(weekStart, 6);
  return {
    start: today > weekStart ? today : weekStart,
    end: coversUntil && coversUntil > sunday ? coversUntil : sunday,
  };
}

export const selectGroceryRange = (
  state: { groceryList?: { sourceWeekStart?: string | null; coversUntil?: string | null } },
  today: string,
): { start: string; end: string } =>
  groceryRange(selectGroceryWeekStart(state), state.groceryList?.coversUntil ?? null, today);

/** True when the user is looking at a week other than the list's week. */
export const selectIsGroceryWeekPinnedElsewhere = (
  state: { groceryList?: { sourceWeekStart?: string | null }; mealPlan?: { currentWeekStart?: string } },
  viewedWeekStart: string
): boolean => selectGroceryWeekStart(state) !== viewedWeekStart;

/**
 * Run `fn` without competing with an in-flight interaction.
 *
 * Grocery generation is a multi-hundred-millisecond synchronous pass over every
 * ingredient. On native that is enough to drop frames mid-transition, so defer
 * it until gestures and navigation animations have settled.
 *
 * On web it used to run inline (#668), so every "add to week" tap froze the
 * screen for 200–300 ms on the reporting account before the tap's own paint.
 * Yielding past the next frame lets the button and the regenerating state
 * paint first; the list arrives a few milliseconds later.
 */
function runOffInteraction(fn: () => void): void {
  if (Platform.OS === 'web') {
    const raf = typeof requestAnimationFrame === 'function' ? requestAnimationFrame : null;
    if (raf) {
      raf(() => setTimeout(fn, 0));
    } else {
      setTimeout(fn, 0);
    }
    return;
  }
  InteractionManager.runAfterInteractions(fn);
}

/**
 * Compute the grocery list for a week and apply it.
 *
 * `weekStart` omitted → regenerate the currently pinned week (the automatic
 * path, when that week's plan changed). `weekStart` provided → the user
 * explicitly asked to shop for that week, which repoints the list to it.
 *
 * `coversUntil` stretches the list past that week's Sunday (#722). Omitted, an
 * automatic rebuild keeps the range it had and an explicit repoint drops it —
 * shopping for one week means that week.
 */
export const buildGroceryList =
  (options: { weekStart?: string; coversUntil?: string | null } = {}) =>
  (dispatch: (action: unknown) => void, getState: () => any): void => {
    const state = getState();
    const weekStart = options.weekStart ?? selectGroceryWeekStart(state);
    const coversUntil = options.coversUntil !== undefined
      ? options.coversUntil
      : options.weekStart !== undefined ? null : (state.groceryList?.coversUntil ?? null);
    const { start, end } = groceryRange(weekStart, coversUntil, todayIso());
    const meals = selectMealsForDateRange(state, start, end);
    const inventory: InventoryItem[] = state.inventory?.items ?? [];
    const excludedStaples = state.userPreferences?.excludedStaples ?? [];

    // Only recipe meals contribute ingredients (excludes entries and free-text)
    const recipeMeals = meals.filter(isRecipeMeal);

    dispatch(setRegenerating(true));

    runOffInteraction(() => {
      const startedAt = Date.now();
      // The app language, so items whose ingredient text has not been
      // translated yet come back flagged and the UI can mask the name rather
      // than print the wrong language (#626).
      const targetLanguage = state.language?.currentLanguage as 'en' | 'fr' | undefined;
      const { items, alreadyHaveItems, metrics } = generateGroceryList(
        recipeMeals,
        inventory,
        targetLanguage,
        excludedStaples,
      );
      const durationMs = Date.now() - startedAt;

      dispatch(
        setGeneratedList({
          items,
          alreadyHaveItems,
          metrics,
          weekSnapshot: generateWeekSnapshot(recipeMeals),
          sourceWeekStart: weekStart,
          coversUntil,
        })
      );

      trackEvent('grocery_list_generated', {
        duration_ms: durationMs,
        item_count: items.length,
        meal_count: recipeMeals.length,
        explicit: options.weekStart !== undefined,
      });
    });
  };

/**
 * Count of items still to buy (unchecked). Drives the week counter banner —
 * the list is auto-generated from the plan, so this stays consistent with the
 * grocery screen.
 */
export const selectUncheckedGroceryCount = (state: {
  groceryList?: { items?: { isChecked: boolean }[] };
}): number => (state.groceryList?.items ?? []).filter(item => !item.isChecked).length;

export default groceryListSlice.reducer;
